import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  InputWithAction,
  Button,
  Space,
  Paper,
  Toggle,
  UploadFile,
  Text,
  Conditional,
  Popup,
  toast,
  Icon,
} from '@cashfree-intl/coherent';
import FormikComponent from 'components/Formik';
import CustomIcon from 'components/CustomIcons';
import { ICON_NAME } from 'components/CustomIcons/types';
import { exportToCSV } from 'utils/common';

import {
  ACTIVATED,
  checkList,
  COD_CSV_FILTERS,
  DISABLED,
  DISABLED_REASON,
  DISABLED_REASON_MAPPER,
  NOT_CONFIGURABLE_MESSAGE,
  REJECTED,
  SAMPLE_FILE_URL,
  STATUS_COLOR_MAPPER,
  STATUS_MAPPER,
} from '../constants';
import { CODSchema } from '../validation';
import { parseCSV } from '../helpers';
import { StyledUploadSpace } from './styled';

const Cod = ({
  mode,
  attributes,
  isTest,
  onToggle,
  disableToggle,
  onCodSubmit,
  cashOnDeliveryData,
}) => {
  const [serviceData, setServiceData] = useState<any>();
  const [uploaded, setUploaded] = useState(null);
  const handleFormSubmit = data => {
    const pinCodes = serviceData?.pinCode ? serviceData.pinCode : [];
    onCodSubmit({ ...data, COD_ORDER_BLOCKED_PIN_CODES: pinCodes });
  };

  const downloadOrUpdateFile = (isDownladable = true) => {
    if (cashOnDeliveryData.COD_ORDER_BLOCKED_PIN_CODES.length) {
      const pinCodesRow = cashOnDeliveryData.COD_ORDER_BLOCKED_PIN_CODES.map(
        a => ({ pinCode: a }),
      );
      if (isDownladable) {
        exportToCSV('pinCodes', pinCodesRow, true, false);
      } else {
        const files = exportToCSV('pinCodes', pinCodesRow, true, true);
        setUploaded(files);
        setServiceData({
          pinCode: cashOnDeliveryData.COD_ORDER_BLOCKED_PIN_CODES,
        });
      }
    }
  };

  useEffect(() => {
    downloadOrUpdateFile(false);
  }, []);

  const handleFileUpload = payload => {
    const { file } = payload;
    const reader = new FileReader();

    return new Promise((res, rej) => {
      reader.onload = e => {
        try {
          const text = e.target.result;
          const parsedData = parseCSV(text, COD_CSV_FILTERS);
          setUploaded(file);
          res(parsedData);
        } catch (error) {
          setUploaded(null);
          toast.error(error.message);
          rej(error);
        }
      };
      reader.readAsText(file);
    });
  };

  const handleDownloadSample = () => {
    window.open(SAMPLE_FILE_URL, '_blank');
  };

  return (
    <FormikComponent
      initialValues={cashOnDeliveryData}
      onSubmit={handleFormSubmit}
      validationSchema={CODSchema}
      enableReinitialize
    >
      {({
        values,
        handleFieldBlur,
        handleFieldChange,
        handleSubmit,
        errors,
        touched,
      }) => (
        <Paper>
          <Space
            alignItems="center"
            justifyContent="flex-end"
            gap={3}
            className="mb-3"
          >
            {/* for DISABLED, REJECTED, IN_REVIEW -> opacity and disable the toggle */}
            {/* AVAILABLE and ACTIVATED ->  */}

            <Conditional
              if={
                STATUS_COLOR_MAPPER[mode.status] &&
                (!isTest || mode.status?.trim().toUpperCase() === ACTIVATED) &&
                !(mode.status?.trim().toUpperCase() === DISABLED)
              }
            >
              <Space alignItems="center">
                <Text color={STATUS_COLOR_MAPPER[mode.status]}>
                  {STATUS_MAPPER[mode.status]}
                </Text>
                <Conditional if={[REJECTED, DISABLED].includes(mode.status)}>
                  <Popup
                    content={DISABLED_REASON.replace(
                      '$reason$',
                      `${DISABLED_REASON_MAPPER[mode.reason?.toUpperCase()] ||
                        ''}`,
                    )}
                    trigger={
                      <span className="info pointer reason ml-1">
                        <CustomIcon componentName={ICON_NAME.INFO} />
                      </span>
                    }
                    position="top right"
                  />
                </Conditional>
              </Space>
            </Conditional>
            <Conditional if={!isTest}>
              <Conditional if={mode.isConfigurable}>
                <div data-testid="toggle">
                  <Toggle
                    disabled={disableToggle}
                    active={mode.status === ACTIVATED}
                    onToggle={onToggle(mode, attributes)}
                  />
                </div>
              </Conditional>
              <Conditional if={!mode.isConfigurable}>
                <Popup
                  content={NOT_CONFIGURABLE_MESSAGE}
                  trigger={
                    <div>
                      <Toggle
                        disabled
                        active={mode.status === ACTIVATED}
                        onToggle={onToggle(mode, attributes)}
                      />
                    </div>
                  }
                  position="top right"
                />
              </Conditional>
            </Conditional>
          </Space>
          <Text variant="h16" className="mb-3">
            Amount Details
          </Text>
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Space gap={9}>
              <Form.Field>
                <Text variant="b14" color="bodyLight" className="mb-1">
                  Min Order Amount
                </Text>
                <InputWithAction
                  className="input-field-with-text-label mb-0"
                  // ${discountError ? 'error_border' : ''}`}
                  name="COD_ORDER_MINIMUM_AMOUNT"
                  value={values.COD_ORDER_MINIMUM_AMOUNT}
                  placeholder="00.00"
                  label={{
                    basic: true,
                    content: '₹',
                  }}
                  allowedValues="numericWithDots"
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  errors={errors}
                  touched={touched}
                  type="number"
                />
              </Form.Field>
              <Form.Field>
                <Text variant="b14" color="bodyLight" className="mb-1">
                  Max Order Amount
                </Text>
                <InputWithAction
                  className="input-field-with-text-label mb-0"
                  name="COD_ORDER_MAXIMUM_AMOUNT"
                  value={values.COD_ORDER_MAXIMUM_AMOUNT}
                  placeholder="00.00"
                  label={{
                    basic: true,
                    content: '₹',
                  }}
                  allowedValues="numericWithDots"
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  errors={errors}
                  touched={touched}
                  type="number"
                />
              </Form.Field>
              <Form.Field>
                <Text variant="b14" color="bodyLight" className="mb-1">
                  COD Handling Fee
                </Text>
                <InputWithAction
                  className="input-field-with-text-label mb-0"
                  name="COD_HANDLING_CHARGES"
                  value={values.COD_HANDLING_CHARGES}
                  placeholder="00.00"
                  label={{
                    basic: true,
                    content: '₹',
                  }}
                  allowedValues="numericWithDots"
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  errors={errors}
                  touched={touched}
                  type="number"
                />
              </Form.Field>
            </Space>

            <Space direction="column" className="cod-form-wrapper">
              <Text variant="h16" className="mb-2 mt-4">
                COD Non Serviceability
              </Text>
              <Space justifyContent="space-between" alignItems="center">
                <Text color="bodyLight">Cod Additional Info</Text>
                <Button
                  variant="link"
                  onClick={handleDownloadSample}
                  type="button"
                >
                  Download Sample File
                </Button>
              </Space>
              <StyledUploadSpace gap="2" alignItems="center">
                <UploadFile
                  accept=".csv"
                  sizeLimit={500000}
                  checkList={checkList}
                  onFileUpload={handleFileUpload}
                  onError={error => console.log(error)}
                  onChange={response => {
                    if (Object.keys(response).length === 0) {
                      setUploaded(null);
                    }
                    setServiceData(response);
                  }}
                  uploaded={uploaded}
                />
                {cashOnDeliveryData.COD_ORDER_BLOCKED_PIN_CODES.length ? (
                  <Icon
                    name="download"
                    onClick={downloadOrUpdateFile}
                    className="pointer"
                  />
                ) : null}
              </StyledUploadSpace>
            </Space>
            <Space gap={1} alignItems="center" className="mt-2">
              <Button
                primary
                type="submit"
                disabled={mode.status !== ACTIVATED}
              >
                Submit
              </Button>
              <Conditional if={mode.status !== ACTIVATED}>
                <Popup
                  position="right center"
                  content={
                    <Text variant="b12" color="bodyLight">
                      You can only update post activation
                    </Text>
                  }
                  trigger={
                    <span>
                      <Icon name="info-small" fill="#bf4b07" />
                    </span>
                  }
                />
              </Conditional>
            </Space>
          </Form>
        </Paper>
      )}
    </FormikComponent>
  );
};

Cod.propTypes = {
  mode: PropTypes.object.isRequired,
  attributes: PropTypes.object.isRequired,
  isTest: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  disableToggle: PropTypes.bool.isRequired,
  onCodSubmit: PropTypes.func.isRequired,
  cashOnDeliveryData: PropTypes.object.isRequired,
};

export default Cod;
